import { EnumUserPermission } from "@/app/services/generated/enums/enumUserPermission.generated";
import EnumUserPermissionAccess from "@/components/pages/administration/user_roles/EnumUserPermissionAccess";
import userStore from "@/store/modules/userStore";
import type Key from "@/app/types/common/key";
import { keys } from "@/plugins/i18n";

type UserPermissionsViewEditOrFullAccess = {
  view: EnumUserPermission;
  edit: EnumUserPermission;
};

type ViewEditUserPermissionGroupsOptions = {
  groupName: Key;
  groupDescription: Key;
  permissions: UserPermissionsViewEditOrFullAccess;
};

type UserPermissionGroupsOptions = {
  groupName: Key;
  groupDescription: Key;
  permissions: EnumUserPermission[];
};

interface IUserPermissionGroup {
  // TODO - this should probably just be string
  groupName: Key;
  // TODO - this should probably just be string
  groupDescription: Key;
  allPermissions: EnumUserPermission[];
}

export class ViewEditUserPermissionGroup implements IUserPermissionGroup {
  private _viewPermission: EnumUserPermission;
  private _editPermission: EnumUserPermission;
  private _groupName: Key;
  private _groupDescription: Key;

  constructor(options: ViewEditUserPermissionGroupsOptions) {
    this._groupName = options.groupName;
    this._groupDescription = options.groupDescription;
    this._viewPermission = options.permissions.view;
    this._editPermission = options.permissions.edit;
  }

  get groupName() {
    return this._groupName;
  }

  get groupDescription() {
    return this._groupDescription;
  }

  get viewPermission() {
    return this._viewPermission;
  }

  get editPermission() {
    return this._editPermission;
  }

  get allPermissions() {
    return [this._viewPermission, this._editPermission];
  }

  evaluateAccess(permissions: EnumUserPermission[]): EnumUserPermissionAccess {
    const permissionsSet = new Set(
      permissions.map((permission) => permission.key)
    );

    if (permissionsSet.has(this._editPermission.key)) {
      return EnumUserPermissionAccess.fullAccess;
    }

    if (permissionsSet.has(this._viewPermission.key)) {
      return EnumUserPermissionAccess.readOnly;
    }

    return EnumUserPermissionAccess.noAccess;
  }
}

export class UserPermissionGroup implements IUserPermissionGroup {
  private _groupName: Key;
  private _groupDescription: Key;
  private _allPermissions: EnumUserPermission[];

  constructor(options: UserPermissionGroupsOptions) {
    this._groupName = options.groupName;
    this._groupDescription = options.groupDescription;
    this._allPermissions = options.permissions;
  }

  get groupName() {
    return this._groupName;
  }

  get groupDescription() {
    return this._groupDescription;
  }

  get allPermissions() {
    return this._allPermissions;
  }
}

export const groupIsViewEditUserPermissionGroup = (
  group: ViewEditUserPermissionGroup | UserPermissionGroup
): group is ViewEditUserPermissionGroup =>
  (group as any).viewPermission !== undefined;

export const allMenuSecurityEnums = [
  EnumUserPermission.menuCalendarView,
  EnumUserPermission.menuTasksView,
  EnumUserPermission.menuSalesView,
  EnumUserPermission.menuContactsView,
  EnumUserPermission.menuStockView,
  EnumUserPermission.menuDistributionView,
  EnumUserPermission.menuProductionView,
  EnumUserPermission.menuUllagesView,
  EnumUserPermission.menuPricingView,
  EnumUserPermission.menuContainerTrackingView,
  EnumUserPermission.menuDutyView,
  EnumUserPermission.menuReportingView,
  EnumUserPermission.menuIntegrationsView,
  EnumUserPermission.menuAdministrationView,
  EnumUserPermission.menuDistributionAppView,
  EnumUserPermission.menuMultiTenantView,
];

const orders = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_orders,
  groupDescription: keys.user_roles_orders_description,
  permissions: {
    view: EnumUserPermission.ordersView,
    edit: EnumUserPermission.ordersEdit,
  },
});
const creditNotes = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_credit_notes,
  groupDescription: keys.user_roles_credit_notes_description,
  permissions: {
    view: EnumUserPermission.creditNotesView,
    edit: EnumUserPermission.creditNotesEdit,
  },
});
const outletsAndGroups = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_outlets_and_groups,
  groupDescription: keys.user_roles_outlets_and_groups_description,
  permissions: {
    view: EnumUserPermission.outletsAndGroupsView,
    edit: EnumUserPermission.outletsAndGroupsEdit,
  },
});
const distributionManagement = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_distribution_management,
  groupDescription: keys.user_roles_distribution_management_description,
  permissions: {
    view: EnumUserPermission.distributionManagementView,
    edit: EnumUserPermission.distributionManagementView, // @TODO: Update when edit permissions are set
  },
});
const distributionProcessing = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_distribution_processing,
  groupDescription: keys.user_roles_distribution_processing_description,
  permissions: {
    view: EnumUserPermission.distributionProcessingView,
    edit: EnumUserPermission.distributionProcessingView, // @TODO: Update when edit permissions are set
  },
});
const distributionApp = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_distribution_app,
  groupDescription: keys.user_roles_distribution_app_description,
  permissions: {
    view: EnumUserPermission.distributionApp,
    edit: EnumUserPermission.distributionApp,
  },
});
const priceLists = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_price_lists,
  groupDescription: keys.user_roles_price_lists_description,
  permissions: {
    view: EnumUserPermission.priceListsView,
    edit: EnumUserPermission.priceListsEdit,
  },
});
const reporting = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_reporting,
  groupDescription: keys.user_roles_reporting_description,
  permissions: {
    view: EnumUserPermission.reporting,
    edit: EnumUserPermission.reporting,
  },
});
const administration = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_administration,
  groupDescription: keys.user_roles_administration_description,
  permissions: {
    view: EnumUserPermission.administration,
    edit: EnumUserPermission.administration,
  },
});
const assemblies = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_assemblies,
  groupDescription: keys.user_roles_assemblies_description,
  permissions: {
    view: EnumUserPermission.batchesView,
    edit: EnumUserPermission.batchesEdit,
  },
});
const recipes = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_recipes,
  groupDescription: keys.user_roles_recipes_description,
  permissions: {
    view: EnumUserPermission.recipesView,
    edit: EnumUserPermission.recipesEdit,
  },
});
const vessels = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_vessels,
  groupDescription: keys.user_roles_vessels_description,
  permissions: {
    view: EnumUserPermission.vesselsView,
    edit: EnumUserPermission.vesselsEdit,
  },
});
const menuSecurity = new UserPermissionGroup({
  groupName: keys.user_roles_menu_security,
  groupDescription: keys.user_roles_menu_security_description,
  permissions: allMenuSecurityEnums,
});
const multiTenant = new ViewEditUserPermissionGroup({
  groupName: keys.user_roles_multi_tenant,
  groupDescription: keys.user_roles_multi_tenant_description,
  permissions: {
    view: EnumUserPermission.multiTenant,
    edit: EnumUserPermission.multiTenant,
  },
});

export const allPermissionGroups: (
  | ViewEditUserPermissionGroup
  | UserPermissionGroup
)[] = [
  orders,
  creditNotes,
  outletsAndGroups,
  distributionManagement,
  distributionProcessing,
  distributionApp,
  vessels,
  assemblies,
  recipes,
  priceLists,
  reporting,
  administration,
  menuSecurity,
  multiTenant,
];

export function checkUserPermissions(
  existingPermissions: Readonly<EnumUserPermission[]>,
  requiredPermissions: Readonly<EnumUserPermission[]>
): boolean {
  if (
    userStore.state.user?.readOnly.isSupportUser ||
    userStore.state.user?.readOnly.isSystemUser
  ) {
    return true;
  }

  // True admins are immune to checks
  if (
    userStore.hasAdministrativePrivileges.value &&
    !requiredPermissions.includes(EnumUserPermission.multiTenant) &&
    !requiredPermissions.includes(EnumUserPermission.menuMultiTenantView)
  ) {
    return true;
  }

  // Compare against key values, as object instances will likely not be the same
  return requiredPermissions.every((requiredPermission) =>
    existingPermissions.find(
      (existingPermission) => existingPermission.key === requiredPermission.key
    )
  );
}

export function hasPermissionsUsingUserStore(
  requiredPermissions: EnumUserPermission[]
): boolean {
  return checkUserPermissions(
    userStore.state.userPermissions,
    requiredPermissions
  );
}
